<template>
  <!-- The contract page menu's dialogs: end or reopen, move to another third party, delete (US 9371, like the
       system's retire and delete in US 9370) -->
  <div>
    <!-- End -->
    <b-modal ref="end"
             hide-header
             hide-footer
             modal-class="sx-confirm"
             qid="contract-end-dialog">
      <div class="sx-confirm__head">
        <span class="sx-confirm__icon is-calm"><font-awesome-icon icon="archive"/></span>
        <div>
          <h3>{{ $t('contract_end.title', { name: contract.name }) }}</h3>
          <p>{{ $t('contract_end.text') }}</p>
        </div>
      </div>
      <div class="sx-confirm__body">
        <div class="v2-field">
          <label class="v2-field__label" for="contract-ended-at">{{ $t('contract_end.date') }}</label>
          <b-form-input id="contract-ended-at"
                        v-model="endedAt"
                        type="date"
                        :max="today"
                        qid="contract-end-date"></b-form-input>
        </div>
        <p v-if="linkedSystems" class="sp-muted">{{ $tc('contract_end.systems', linkedSystems, { count: linkedSystems }) }}</p>
      </div>
      <div class="sx-confirm__foot">
        <b-button variant="secondary" qid="contract-end-cancel" @click="$refs.end.hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button variant="success"
                  :disabled="busy || !endedAt"
                  qid="contract-end-submit"
                  @click="end">
          <font-awesome-icon :icon="busy ? 'spinner' : 'archive'" :class="{ 'fa-spin': busy }"/> {{ $t('contract_end.submit') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Move to another third party -->
    <b-modal ref="move"
             hide-header
             hide-footer
             modal-class="sx-confirm"
             qid="contract-move-dialog"
             @shown="loadVendors('')">
      <div class="sx-confirm__head">
        <span class="sx-confirm__icon is-calm"><font-awesome-icon icon="exchange-alt"/></span>
        <div>
          <h3>{{ $t('contract_move.title', { name: contract.name }) }}</h3>
          <p>{{ currentNames ? $t('contract_move.text', { name: currentNames }) : $t('contract_move.text_none') }}</p>
        </div>
      </div>
      <div class="sx-confirm__body">
        <div class="v2-field">
          <label class="v2-field__label">{{ $t('contract_move.to') }}</label>
          <multiselect v-model="target"
                       :options="vendorOptions"
                       :internal-search="false"
                       :loading="loadingVendors"
                       :placeholder="$t('contract_move.search')"
                       :select-label="$t('system.dropdown_select_label')"
                       :selected-label="$t('system.dropdown_selected_label')"
                       :deselect-label="$t('system.dropdown_deselect_label')"
                       label="name"
                       track-by="id"
                       qid="contract-move-vendor"
                       @search-change="loadVendors">
            <template slot="noResult">{{ $t('system.no_results') }}</template>
          </multiselect>
        </div>
        <p class="sp-muted">{{ $t('contract_move.keeps') }}</p>
      </div>
      <div class="sx-confirm__foot">
        <b-button variant="secondary" qid="contract-move-cancel" @click="$refs.move.hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button variant="success"
                  :disabled="busy || !target"
                  qid="contract-move-submit"
                  @click="move">
          <font-awesome-icon :icon="busy ? 'spinner' : 'exchange-alt'" :class="{ 'fa-spin': busy }"/> {{ $t('contract_move.submit') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Delete: what it's linked to, and ending it instead -->
    <b-modal ref="delete"
             hide-header
             hide-footer
             modal-class="sx-confirm"
             qid="contract-delete-dialog"
             @hidden="typed = ''">
      <div class="sx-confirm__head">
        <span class="sx-confirm__icon"><font-awesome-icon icon="trash-alt"/></span>
        <div>
          <h3 qid="delete-tp-title">{{ $t('contract_delete.title', { name: contract.name }) }}</h3>
          <p v-if="total">{{ $tc('contract_delete.linked', total, { count: total }) }}</p>
          <p v-else>{{ $t('contract_delete.not_linked') }}</p>
        </div>
      </div>
      <div class="sx-confirm__body">
        <div v-if="impact.length" class="sx-impact">
          <div v-for="item in impact" :key="item.key"><b>{{ item.count }}</b>{{ $tc('contract_delete.counts.' + item.key, item.count) }}</div>
        </div>
        <template v-if="total && canEnd">
          <button type="button"
                  class="sx-option"
                  :class="{ on: choice === 'end' }"
                  qid="contract-delete-choose-end"
                  @click="choice = 'end'">
            <font-awesome-icon icon="archive"/>
            <span class="sx-option__grow"><b>{{ $t('contract_delete.end') }}</b><small>{{ $t('contract_delete.end_hint') }}</small></span>
          </button>
          <button type="button"
                  class="sx-option"
                  :class="{ on: choice === 'delete' }"
                  qid="contract-delete-choose-delete"
                  @click="choice = 'delete'">
            <font-awesome-icon icon="trash-alt"/>
            <span class="sx-option__grow"><b>{{ $t('contract_delete.delete') }}</b><small>{{ $t('contract_delete.delete_hint') }}</small></span>
          </button>
        </template>
        <div v-if="choice === 'delete' && total" class="v2-field">
          <label class="v2-field__label" for="contract-delete-name">{{ $t('system_delete.type_name') }}</label>
          <b-form-input id="contract-delete-name"
                        v-model="typed"
                        autocomplete="off"
                        :placeholder="contract.name"
                        qid="delete-tp-type-name"></b-form-input>
        </div>
      </div>
      <div class="sx-confirm__foot">
        <b-button variant="secondary" qid="delete-tp-cancel-button" @click="$refs.delete.hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button v-if="choice === 'end'" variant="success" qid="contract-delete-end-button" @click="endInstead">
          <font-awesome-icon icon="archive"/> {{ $t('contract_end.submit') }}
        </b-button>
        <b-button v-else variant="danger" :disabled="busy || (total > 0 && typed.trim() !== contract.name.trim())" qid="delete-tp-submit-button" @click="remove">
          <font-awesome-icon :icon="busy ? 'spinner' : 'trash-alt'" :class="{ 'fa-spin': busy }"/> {{ $t('systems.delete.submit') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';

  // What a contract can be linked to, in the order the delete dialog lists them
  const IMPACT = ['systems', 'activities', 'sub_processors', 'documents', 'risks', 'measures'];

  export default {
    name: 'ContractActions',
    props: {
      contract: { type: Object, required: true },
      // The Overview's summary, for what the contract is linked to
      summary: { type: Object, default: null }
    },
    data() {
      return {
        busy: false,
        endedAt: '',
        target: null,
        vendorOptions: [],
        loadingVendors: false,
        searchTimer: null,
        choice: 'delete',
        typed: ''
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      today() {
        return moment().format('YYYY-MM-DD');
      },
      canEnd() {
        return !this.contract.ended_at && this.isGranted([this.permissions.THIRD_PARTY_EDIT, this.permissions.THIRD_PARTY_EDIT_OWN], this.contract);
      },
      counts() {
        return (this.summary && this.summary.counts) || {};
      },
      linkedSystems() {
        return this.counts.systems || 0;
      },
      impact() {
        return IMPACT.filter(key => this.counts[key]).map(key => ({ key: key, count: this.counts[key] }));
      },
      total() {
        return this.impact.reduce((sum, item) => sum + item.count, 0);
      },
      currentNames() {
        return (this.contract.vendors || []).map(vendor => vendor.name).join(', ');
      }
    },
    methods: {
      showEnd() {
        this.endedAt = this.today;
        this.$refs.end.show();
      },
      showMove() {
        this.target = null;
        this.$refs.move.show();
      },
      showDelete() {
        this.choice = this.total && this.canEnd ? 'end' : 'delete';
        this.$refs.delete.show();
      },
      loadVendors(query) {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          const current = (this.contract.vendors || []).map(vendor => vendor.id);
          this.loadingVendors = true;
          piincHttp.get('vendors', { params: {
            client: this.getClient.slug,
            search: query || undefined,
            parent: this.getClient.parent ? 1 : undefined,
            per_page: 100
          } }).then(response => {
            this.loadingVendors = false;
            this.vendorOptions = (response.data.items || []).filter(vendor => current.indexOf(vendor.id) === -1);
          }, () => {
            this.loadingVendors = false;
          });
        }, query ? 250 : 0);
      },
      end() {
        this.busy = true;
        piincHttp.put('third-parties/' + this.contract.slug + '/end', { ended_at: this.endedAt }).then(() => {
          this.busy = false;
          this.$refs.end.hide();
          this.$toastr('success', this.$t('contract_end.saved'));
          this.$emit('changed');
        }, () => {
          this.busy = false;
        });
      },
      endInstead() {
        this.$refs.delete.hide();
        this.showEnd();
      },
      reopen() {
        piincHttp.put('third-parties/' + this.contract.slug + '/reopen').then(() => {
          this.$toastr('success', this.$t('contract_end.reopened'));
          this.$emit('changed');
        }, () => {});
      },
      move() {
        this.busy = true;
        piincHttp.put('third-parties/' + this.contract.slug + '/counterparty', { vendor_id: this.target.id }).then(() => {
          this.busy = false;
          this.$refs.move.hide();
          this.$toastr('success', this.$t('contract_move.saved', { name: this.target.name }));
          this.$emit('changed');
        }, () => {
          this.busy = false;
        });
      },
      remove() {
        this.busy = true;
        piincHttp.delete('third-parties/' + this.contract.slug).then(() => {
          this.busy = false;
          this.$refs.delete.hide();
          this.$store.commit('storeUpdateTasks', true);
          this.$store.commit('storeActiveTask', {});
          this.$toastr('success', this.$t('maintenance.third_parties.delete.success'));
          this.$router.push({ name: 'contracts.third_parties' });
        }, () => {
          this.busy = false;
        });
      }
    }
  }
</script>
